import { useHeaderDetails } from '@shared/header';
import React, { useMemo } from 'react';
import { HiOutlineTrash } from 'react-icons/hi2';

import { useFilters } from '@/components/filters';
import { TableEmptyPlaceholder, TableLoadingOverlay } from '@/components/loading';
import { Button } from '@/components-new/button';
import { PageTitleRow } from '@/components-new/page-title-row';
import { useClearNotificationHistory } from '@/features/notification-history/api/use-clear-notification-history';
import { useNotificationHistoryQuery } from '@/features/notification-history/api/use-notification-history-query';
import { NotificationHistoryFilters } from '@/features/notification-history/components/notification-history-filters';
import { NotificationHistoryTable } from '@/features/notification-history/components/notification-history-table';
import {
  defaultNotificationHistoryFilters,
  NotificationHistoryFilterData
} from '@/features/notification-history/types/notification-history-filter-data';

const NotificationHistoryPage = () => {
  const { setHeaderDetails, clearHeaderDetails } = useHeaderDetails();
  const { data: entries = [], isLoading } = useNotificationHistoryQuery();
  const clearHistory = useClearNotificationHistory();

  const [filters, setFilters] = useFilters<NotificationHistoryFilterData>('notification-history', defaultNotificationHistoryFilters);

  const filteredEntries = useMemo(() => {
    const { recipient, subject, channel, sentFrom, sentTo } = filters;

    return entries.filter(entry => {
      if (channel && channel !== 'all' && entry.channel !== channel) {
        return false;
      }

      if (recipient) {
        const term = recipient.toLowerCase();
        const matchesRecipient = entry.recipient.toLowerCase().includes(term)
          || (entry.recipientName?.toLowerCase().includes(term) ?? false);

        if (!matchesRecipient) {
          return false;
        }
      }

      if (subject && !entry.subject.toLowerCase().includes(subject.toLowerCase())) {
        return false;
      }

      if (sentFrom && new Date(entry.sentAt) < new Date(sentFrom)) {
        return false;
      }

      if (sentTo) {
        const endOfDay = new Date(sentTo);
        endOfDay.setHours(23, 59, 59, 999);

        if (new Date(entry.sentAt) > endOfDay) {
          return false;
        }
      }

      return true;
    });
  }, [entries, filters]);

  React.useEffect(() => {
    setHeaderDetails({
      documentTitle: 'Notification History',
      pageTitle: (
        <PageTitleRow
          title="Notification History"
          subtitle="Email, push, and in-app notifications that have gone out for testers to verify. Email/push rows only appear when Email:Type/Expo:Type are set to Test."
        >
          <Button outline onClick={() => clearHistory.mutate()} disabled={clearHistory.isPending}>
            <HiOutlineTrash className="size-5"/>
            Clear Test Data
          </Button>
        </PageTitleRow>
      ),
      breadcrumbs: []
    });

    return () => {
      clearHeaderDetails();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <TableLoadingOverlay/>;
  }

  if (entries.length === 0) {
    return <TableEmptyPlaceholder content="No notifications have been recorded yet."/>;
  }

  return (
    <>
      <NotificationHistoryFilters defaultValue={filters} onApplyFilters={setFilters}/>
      {filteredEntries.length === 0
        ? <TableEmptyPlaceholder content="No notifications match the current filters."/>
        : <NotificationHistoryTable entries={filteredEntries}/>}
    </>
  );
};

export default NotificationHistoryPage;
